import { useState, forwardRef, type ReactNode } from 'react';

export interface PressableProps {
  onPress: () => void;
  children: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
}

export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(function Pressable({
  onPress,
  children,
  disabled = false,
  fullWidth = false,
  ariaLabel,
  ariaExpanded,
}, ref) {
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);
  const [focused, setFocused] = useState(false);

  const bg = disabled
    ? 'transparent'
    : active
      ? 'var(--evo-bg-3)'
      : hovered
        ? 'var(--evo-bg-2)'
        : 'transparent';

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      onClick={disabled ? undefined : onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : 'auto',
        padding: 0,
        margin: 0,
        background: bg,
        border: 'none',
        borderRadius: 'var(--evo-r-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'inherit',
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        // Los navegadores NO heredan estas dos en elementos de formulario: su
        // hoja por defecto pone `text-transform: none` en <button>, y ahí se
        // corta la cadena. Por eso hay que pedirlas a mano, igual que las tres
        // de arriba.
        //
        // Lo que rompía (visto 2026-09-03): los divisores de fecha de TODAS las
        // listas salían "This month" en vez de "THIS MONTH". El estilo estaba
        // puesto en ListBandHeader, pero el texto vive adentro de este botón
        // —el que hace plegable la banda— y se perdía en el camino.
        textTransform: 'inherit',
        letterSpacing: 'inherit',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
        boxShadow: focused ? '0 0 0 2px var(--evo-cyan)' : 'none',
        transition: 'background 120ms ease-out, opacity 120ms linear, box-shadow 120ms ease-out',
        userSelect: 'none',
        boxSizing: 'border-box',
      }}
    >
      {children}
    </button>
  );
});
